<template>
  <div>
    <div class="header-container">
      <h3>
        <strong>Drugs</strong>
      </h3>
      <a-button type="primary" @click="openAddModal">
        <template #icon><PlusOutlined /></template>
        Add Drugs
      </a-button>
    </div>

    <a-table
      :columns="columns"
      :data-source="drugList"
      :loading="isLoading"
      :row-key="record => record.id"
      :row-class-name="getRowClassName"
    >
      <!-- Slot untuk kolom 'No' -->
      <template #no="{ record }">
        <span>{{ record.no }}</span>
      </template>

      <!-- Slot untuk kolom 'Product Information' -->
      <template #productInformation="{ record }">
        <a
          v-if="record.productInformation"
          :href="
            `${API_BASE_URL}/uploads/${record.productInformation.replace(
              'public/uploads/pdfs/',
              '',
            )}`
          "
          target="_blank"
          rel="noopener noreferrer"
        >
          <FilePdfOutlined /> {{ record.originalFileName }}
        </a>
        <span v-else>-</span>
      </template>

      <!-- Slot untuk kolom 'Action' -->
      <template #action="{ record }">
        <a-button type="primary" @click="openEditModal(record)">
          <template #icon><EditOutlined /></template>
          Edit Data
        </a-button>
      </template>

      <template #filterIcon="{ filtered }">
        <search-outlined :style="{ color: filtered ? '#1890ff' : undefined }" />
      </template>

      <template #filterDropdown="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }">
        <div style="padding: 8px">
          <a-input
            ref="searchInput"
            :placeholder="`Search ${column.dataIndex}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              e => {
                setSelectedKeys(e.target.value ? [e.target.value] : [])

                clearTimeout(debounceTimer)
                debounceTimer = setTimeout(() => {
                  confirm()
                }, 300)
              }
            "
            @pressEnter="confirm()"
          />
          <a-button
            type="primary"
            size="small"
            style="width: 90px; margin-right: 8px"
            @click="handleSearch(selectedKeys, confirm, column.dataIndex)"
          >
            <template #icon><SearchOutlined /></template>
            Search
          </a-button>
          <a-button size="small" style="width: 90px" @click="handleReset(clearFilters, confirm)">
            Reset
          </a-button>
        </div>
      </template>
    </a-table>
    <a-modal
      :visible="isModalVisible"
      :title="modalMode === 'add' ? 'Add Drugs' : 'Update Drugs'"
      width="800px"
      :footer="null"
      :closable="true"
      :mask-closable="false"
      @cancel="handleCancel"
      centered
    >
      <a-form ref="formRef" :model="formState" layout="vertical" @finish="handleSave">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Drugs Name"
              name="name"
              :rules="[{ required: true, message: 'Please input the drug name!' }]"
            >
              <a-input v-model:value="formState.name" placeholder="Enter drug name" />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Dose and route"
              name="doseAndRoute"
              :rules="[{ required: true, message: 'Please input the dose and route!' }]"
            >
              <a-input v-model:value="formState.doseAndRoute" placeholder="Enter dose and route" />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Administration"
              name="administration"
              :rules="[{ required: true, message: 'Please input the administration details!' }]"
            >
              <a-textarea
                v-model:value="formState.administration"
                :rows="4"
                placeholder="Enter administration details"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item
              label="Cycle length"
              name="cycleLength"
              :rules="[{ required: true, message: 'Please input the cycle length!' }]"
            >
              <a-textarea
                v-model:value="formState.cycleLength"
                :rows="4"
                placeholder="Enter cycle length information"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item
              label="Gives on days"
              name="givesOnDays"
              :rules="[{ required: true, message: 'Please input the days when drug is given!' }]"
            >
              <a-input
                v-model:value="formState.givesOnDays"
                placeholder="Enter days when drug is given"
              />
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="Product Information" name="productInformation">
              <a-upload
                v-model:file-list="fileList"
                name="productInformation"
                :max-count="1"
                :before-upload="() => false"
              >
                <a-button v-if="fileList.length === 0">
                  <UploadOutlined /> Click to Upload
                </a-button>
              </a-upload>
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="24">
            <a-form-item label="URL To Study" name="urlToStudy">
              <a-input
                v-model:value="formState.urlToStudy"
                placeholder="Enter study URL (optional)"
              />
            </a-form-item>
          </a-col>
        </a-row>

        <div class="modal-footer">
          <a-button
            @click="handleCancel"
            size="large"
            style=" color: #4C749F; border-radius: 4px; width: 84px;"
            >Cancel</a-button
          >
          <a-button
            v-if="modalMode === 'edit'"
            style="background-color: #F9C6C6; color: #9F4351; border-radius: 4px; width: 84px;"
            size="large"
            @click="handleToggleDelete"
            >Delete</a-button
          >
          <a-button
            type="primary"
            html-type="submit"
            style="background-color: #436894; color: white; border-radius: 4px; width: 84px;"
            size="large"
            >Save</a-button
          >
        </div>
      </a-form>
    </a-modal>
    <a-modal v-model:visible="toggleDelete" title="Delete Drugs" @ok="handleDelete" centered>
      Are you sure you want to delete this drug?
    </a-modal>

    <!-- Cancel Confirmation Modal -->
    <a-modal
      v-model:visible="cancelConfirmVisible"
      title="Unsaved Changes"
      @ok="confirmCancel"
      @cancel="cancelConfirmVisible = false"
      centered
    >
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <span>You have unsaved changes. Are you sure you want to close without saving?</span>
      </div>
      <p>
        All changes will be lost if you continue.
      </p>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { notification, Modal } from 'ant-design-vue'
import {
  FilePdfOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons-vue'
import { DrugAPI } from '../../../services/axios/istry/pathwayService' // Sesuaikan path jika perlu
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://cms-istry.digitala.id'
const drugList = ref([])
const isLoading = ref(true)
const isModalVisible = ref(false)
const cancelConfirmVisible = ref(false)
const modalMode = ref('add')
const editingDrugId = ref(null)
const fileList = ref([]) // Untuk komponen upload
const formRef = ref() // Untuk validasi form

const initialFormState = {
  name: '',
  doseAndRoute: '',
  administration: '',
  cycleLength: '',
  givesOnDays: '',
  urlToStudy: null,
}
let formState = reactive({ ...initialFormState })
const originalFormState = ref({ ...initialFormState })

// Track if form has changes
const hasFormChanges = computed(() => {
  // Check form state changes (excluding fileList from originalFormState)
  const currentFormData = {
    name: formState.name,
    doseAndRoute: formState.doseAndRoute,
    administration: formState.administration,
    cycleLength: formState.cycleLength,
    givesOnDays: formState.givesOnDays,
    urlToStudy: formState.urlToStudy,
  }

  const originalFormData = {
    name: originalFormState.value.name || '',
    doseAndRoute: originalFormState.value.doseAndRoute || '',
    administration: originalFormState.value.administration || '',
    cycleLength: originalFormState.value.cycleLength || '',
    givesOnDays: originalFormState.value.givesOnDays || '',
    urlToStudy: originalFormState.value.urlToStudy || null,
  }

  const formChanged = JSON.stringify(currentFormData) !== JSON.stringify(originalFormData)

  // Check file list changes - compare the actual file names/status
  const currentFiles = fileList.value.map(file => ({
    name: file.name,
    status: file.status,
    uid: file.uid,
  }))

  const originalFiles = (originalFormState.value.fileList || []).map(file => ({
    name: file.name,
    status: file.status,
    uid: file.uid,
  }))

  const filesChanged = JSON.stringify(currentFiles) !== JSON.stringify(originalFiles)

  return formChanged || filesChanged
})
const columns = [
  {
    title: 'No',
    dataIndex: 'no',
    key: 'no',
    width: 70,
    slots: { customRender: 'no' }, // Definisikan slot
  },
  {
    title: 'Drug Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a, b) => a.name.localeCompare(b.name),
    slots: {
      filterDropdown: 'filterDropdown', // Memberi tahu Vue untuk menggunakan slot bernama 'filterDropdown'
      filterIcon: 'filterIcon', // Memberi tahu Vue untuk menggunakan slot bernama 'filterIcon'
    },
    onFilter: (value, record) =>
      record.name
        .toString()
        .toLowerCase()
        .includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => {
          searchInput.value.focus()
        }, 100)
      }
    },
  },
  {
    title: 'Product Information',
    dataIndex: 'productInformation',
    key: 'productInformation',
    slots: { customRender: 'productInformation' }, // Definisikan slot
    sorter: (a, b) => (a.productInformation || '').localeCompare(b.productInformation || ''),
  },
  {
    title: 'Action',
    key: 'action',
    width: 150,
    slots: { customRender: 'action' }, // Definisikan slot
  },
]

const toggleDelete = ref(false)

const handleToggleDelete = () => {
  toggleDelete.value = true
}
const handleDelete = async () => {
  try {
    // Panggil API yang benar dengan ID yang benar
    await DrugAPI.deleteDrug(editingDrugId.value)

    notification.success({
      message: 'Success',
      description: 'Drug has been deleted successfully.',
    })

    // Muat ulang data tabel dari server
    await fetchDrugs()
  } catch (error) {
    notification.error({
      message: 'Delete Failed',
      description: error.response?.data?.message || 'An error occurred.',
    })
  } finally {
    // Tutup kedua modal
    toggleDelete.value = false
    closeModal()
  }
}

// --- STATE & LOGIKA UNTUK FILTER PENCARIAN ---
const searchText = ref('')
const searchedColumn = ref('')
const searchInput = ref()
let debounceTimer = null

// Fungsi untuk menangani aksi "Search" pada tombol
const handleSearch = (selectedKeys, confirm, dataIndex) => {
  confirm()
  searchText.value = selectedKeys[0]
  searchedColumn.value = dataIndex
}

// Fungsi untuk menangani aksi "Reset"
const handleReset = (clearFilters, confirm) => {
  clearFilters()
  searchText.value = ''
  // Panggil handleSearch dengan nilai kosong untuk me-reset dan mengkonfirmasi filter
  handleSearch([''], confirm, '')
}
// --- API & DATA FUNCTIONS ---
const fetchDrugs = async () => {
  try {
    isLoading.value = true
    const response = await DrugAPI.getAllDrugs()
    console.log(response, '//')
    drugList.value = response.data.map((e, idx) => {
      return {
        ...e,
        no: idx + 1,
      }
    }) // Sesuaikan dengan struktur respons Anda
  } catch (error) {
    notification.error({ message: 'Error', description: 'Could not fetch drugs data.' })
  } finally {
    isLoading.value = false
  }
}

const getRowClassName = (_record, index) => (index % 2 === 1 ? 'table-striped-row' : null)

onMounted(fetchDrugs)

// --- MODAL & FORM LOGIC ---
const openAddModal = () => {
  modalMode.value = 'add'
  Object.assign(formState, initialFormState)
  originalFormState.value = { ...initialFormState, fileList: [] }
  fileList.value = []
  isModalVisible.value = true
}

const openEditModal = record => {
  modalMode.value = 'edit'
  editingDrugId.value = record.id
  const editFormState = {
    name: record.name,
    doseAndRoute: record.doseAndRoute,
    administration: record.administration,
    cycleLength: record.cycleLength,
    givesOnDays: record.givesOnDays,
    urlToStudy: record.urlToStudy,
  }
  Object.assign(formState, editFormState)
  const currentFileList = record.productInformation
    ? [{ uid: '-1', name: record.productInformation.split('/').pop(), status: 'done' }]
    : []
  fileList.value = currentFileList
  originalFormState.value = { ...editFormState, fileList: [...currentFileList] }
  isModalVisible.value = true
}

const handleCancel = () => {
  if (hasFormChanges.value) {
    cancelConfirmVisible.value = true
  } else {
    closeModal()
  }
}

const confirmCancel = () => {
  cancelConfirmVisible.value = false
  closeModal()
}

const closeModal = () => {
  isModalVisible.value = false
  // Reset form state
  Object.assign(formState, initialFormState)
  fileList.value = []
  originalFormState.value = { ...initialFormState, fileList: [] }
  // Clear form validation errors
  if (formRef.value) {
    formRef.value.clearValidate()
  }
}

const handleSave = async () => {
  try {
    // Custom validation for file upload in add mode
    if (modalMode.value === 'add' && fileList.value.length === 0) {
      notification.error({
        message: 'Validation Error',
        description: 'Please upload product information!',
      })
      return
    }

    // Custom validation for file upload in edit mode if no existing file
    if (modalMode.value === 'edit' && fileList.value.length === 0) {
      notification.error({
        message: 'Validation Error',
        description: 'Please upload product information!',
      })
      return
    }

    await formRef.value.validate()
    const formData = new FormData()

    for (const key in formState) {
      if (formState[key]) {
        formData.append(key, formState[key])
      }
    }
    if (fileList.value.length > 0 && fileList.value[0].originFileObj) {
      formData.append('productInformation', fileList.value[0].originFileObj)
    }

    if (modalMode.value === 'add') {
      await DrugAPI.createDrug(formData)
      notification.success({ message: 'Success', description: 'New drug added successfully.' })
    } else {
      await DrugAPI.updateDrug(editingDrugId.value, formData)
      notification.success({ message: 'Success', description: 'Drug updated successfully.' })
    }

    closeModal()
    fetchDrugs()
  } catch (error) {
    notification.error({ message: 'Save Failed', description: 'Please check the form for errors.' })
  }
}
</script>

<style scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.modal-footer {
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.ant-table-thead > tr > th:nth-child(odd)) {
  background-color: #f2faff !important;
  font-weight: 600;
  color: #395b86;
}

:deep(.ant-table-thead > tr > th:nth-child(even)) {
  background-color: #e6f4fc !important;
  font-weight: 600;
  color: #395b86;
}

/* Memberi warna pada baris ganjil yang sudah kita beri kelas */
:deep(.table-striped-row td) {
  background-color: #fafafa !important;
}

/* Mengatur hover effect pada semua baris */
:deep(.ant-table-tbody > tr:hover > td) {
  background-color: #e6f7ff !important;
}

/* (Opsional) Menghilangkan border antar kolom jika ada */
:deep(.ant-table-tbody > tr > td),
:deep(.ant-table-thead > tr > th) {
  border-right: none;
  border-left: none;
}

:deep(.ant-input::placeholder) {
  color: #9ca3af !important;
}
</style>
